import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Pagination,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import plansApi from "../../hooks/GET/plans";
import { endpoint } from "../../config";
import { customToast } from "../../lib/toastLib";

function OneTimePayment() {
  const navigate = useNavigate();

  // get query(search) params logic start
  const [searchParams] = useSearchParams();
  const [totalResult, setTotalResult] = useState(0);

  const oneTimePaymentId = searchParams.get("oneTimePaymentId");
  // get query(search) params logic end

  // oneTimePayment success/failure api based on query params if available logic start
  useEffect(() => {
    if (oneTimePaymentId) {
      axios
        .put(`${endpoint}/api/oneTimePayment/${oneTimePaymentId}`)
        .then((res) => res.data)
        .then((data) => {
          customToast.success(data.message);
          navigate("/dashboard", { replace: true });
        })
        .catch((error) => {
          if (error.response) {
            customToast.error(error.response.data?.message);
          } else {
            customToast.error(error.message);
          }
        });
    }
  }, [oneTimePaymentId]);
  // oneTimePayment success/failure api based on query params if available logic end

  // plan list logic start
  const [planList, setPlanList] = useState([]);

  useEffect(() => {
    plansApi()
      .then((data) => {
        const oneTimePaymentPlans = data.filter(
          (obj) => obj.planBillingInterval === 0
        );
        setPlanList(oneTimePaymentPlans);
      })
      .catch((error) => console.log(error));
  }, []);
  // plan list logic end

  //table pagination logic start
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOption = [5, 10, 20];

  const handleChangePage = (e, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const emptyRows =
  //   rowsPerPage - Math.min(rowsPerPage, planList.length - page * rowsPerPage);
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, totalResult - page * rowsPerPage);
  //table pagination logic end

  // search logic start
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(0);
  };

  const getFilteredPlanList = useMemo(() => {
    let filterdata = planList;
    filterdata = filterdata.filter((obj) =>
      JSON.stringify(obj).match(new RegExp(search, "gi"))
    );

    setTotalResult(filterdata.length);
    if (filterdata.length !== 0) {
      const lastPage = Math.ceil(filterdata.length / rowsPerPage);
      if (lastPage === page)
        setPage(lastPage - 1);
    }
    return filterdata;
  }, [planList, search, rowsPerPage, page]);
  // search logic end

  // handle pay logic start
  const [payBtnLoading, setPayBtnLoading] = useState(null);

  const handlePay = (e, id) => {
    e.preventDefault();

    const requestBody = {
      planId: id,
    };
    setPayBtnLoading(id);
    axios
      .post(`${endpoint}/api/oneTimePayment`, requestBody)
      .then((res) => res.data)
      .then((data) => {
        setPayBtnLoading(null);
        window.location = data.stripeUrl;
      })
      .catch((error) => {
        setPayBtnLoading(null);
        if (error.response) {
          customToast.error(error.response.data?.message);
        } else {
          customToast.error(error.message);
        }
      });
  };
  // handle pay logic end

  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Typography sx={{ display: "contents" }}>Show</Typography>
          <TextField
            select
            name="rowsPerPage"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{ verticalAlign: "middle", margin: 1, minWidth: 75 }}
          >
            {rowsPerPageOption.map((option, idx) => (
              <MenuItem key={idx} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ display: "contents" }}>entries</Typography>
          <TextField
            name="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ verticalAlign: "middle", margin: 1 }}
          />
        </Grid>
      </Grid>

      {/* Table part */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Sr. No.</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">Pay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? getFilteredPlanList.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              : getFilteredPlanList
            ).map((fieldValue, idx) => (
              <TableRow key={idx} hover>
                <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                <TableCell align="center">{fieldValue.planName}</TableCell>
                <TableCell align="center">
                  {fieldValue.planDescription}
                </TableCell>
                <TableCell align="center">
                  <LoadingButton
                    fullWidth
                    color="secondary"
                    variant="contained"
                    disabled={payBtnLoading && payBtnLoading !== fieldValue.id}
                    loading={payBtnLoading === fieldValue.id}
                    onClick={(e) => handlePay(e, fieldValue.id)}
                  >
                    {fieldValue.planCurrecyIsoCode.toLocaleUpperCase()}{" "}
                    {fieldValue.planPrice}
                  </LoadingButton>
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell align="center" colSpan={4}>
                  {/* {planList.length === 0 && "No plan available to display"} */}
                  {totalResult === 0 && "No one-time payment plans available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* {planList.length > 0 && ( */}
      {totalResult > 0 && (
        <Grid container justifyContent="center" sx={{ mt: 2 }} spacing={1}>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page)}
              disabled={page === 0}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Pagination
              // count={Math.ceil(planList.length / rowsPerPage)}
              count={Math.ceil(totalResult / rowsPerPage)}
              page={page + 1}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
              color="secondary"
              hidePrevButton
              hideNextButton
            />
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page + 2)}
              // disabled={Math.ceil(planList.length / rowsPerPage) === page + 1}
              disabled={Math.ceil(totalResult / rowsPerPage) === page + 1}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default OneTimePayment;
